/**
 * 샵관리 — 쿠팡 마진계산기
 * 판매가·원가·수수료·물류비·광고비 등을 넣으면 순이익/마진율/수익률, 손익분기 판매가,
 * 목표 마진율 달성 판매가, 판매가 시나리오를 바로 보여준다. 상품별로 저장해 비교할 수 있다.
 * 수수료율·물류비는 쿠팡 정책에 따라 달라 값을 직접 입력한다(계산 로직: shop/marginCalc.js).
 * 세전 관리용 계산이며 세무 신고 금액이 아니다.
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { PageContainer, ActionButton } from 'styles/common.styles';
import { showAlert } from 'utils/toastBridge';
import { loadTable, saveTable, createUid, stockSummary, fmt, num } from 'shop/shopStore';
import { calcAt, solvePrices, priceLadder } from 'shop/marginCalc';

const TABLE = 'marginItem';

const DEFAULTS = {
  name: '',
  price: '',
  cost: '',
  costVatIncl: true,
  qtyPerSet: '1',
  feeRate: '10.8',
  shipFee: '0',
  adRate: '0',
  packFee: '0',
  returnRate: '0',
  taxMode: 'GENERAL',
  simpleTaxRate: '1.5',
  targetMargin: '20',
  monthlyQty: '100',
};

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 400px) 1fr;
  gap: 12px;
  align-items: start;
  font-size: 12px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  .box { border: 1px solid #d9dde3; border-radius: 4px; background: #fff; padding: 8px 10px; display: flex; flex-direction: column; gap: 6px; }
  .title { font-weight: 700; font-size: 13px; }
  .hint { color: #5b6472; }
  .warn { color: #c0392b; }
  .f { display: grid; grid-template-columns: 120px 1fr; gap: 6px; align-items: center; }
  .f label { color: #3e4756; }
  .f small { grid-column: 2; color: #7a8493; margin-top: -3px; }
  input[type='text'], input[type='number'], select {
    height: 24px; font-size: 12px; border: 1px solid #c5cad3; border-radius: 3px; padding: 0 6px; width: 100%;
  }
  .row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #d9dde3; padding: 3px 6px; }
  th { background: #f3f5f8; }
  td.n { text-align: right; font-variant-numeric: tabular-nums; }
  tr.sub td { background: #fafbfc; font-weight: 600; }
  tr.total td { background: #eef3ff; font-weight: 700; }
  tr.cur td { background: #fffbe8; }
  .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 6px; }
  .kpi { border: 1px solid #d9dde3; border-radius: 4px; padding: 6px 8px; }
  .kpi .l { color: #5b6472; }
  .kpi .v { font-size: 17px; font-weight: 700; margin-top: 2px; }
  .kpi .v.neg { color: #c0392b; }
  .kpi .v.pos { color: #1b7f4b; }
`;

const won = (v) => `${v < 0 ? '−' : ''}₩${fmt(Math.abs(Math.round(v)))}`;
const pctText = (v) => `${(v * 100).toFixed(1)}%`;

const Field = ({ label, hint, children }) => (
  <>
    <label>{label}</label>
    <div>{children}</div>
    {hint && <small>{hint}</small>}
  </>
);

const MarginCalculator = () => {
  const tc = useTenantCode();
  const [f, setF] = useState(DEFAULTS);
  const [items, setItems] = useState(() => loadTable(tc, TABLE));
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const products = useMemo(() => stockSummary(tc).filter((s) => s.useYn !== 'N'), [tc]);

  const price = num(f.price);
  const result = useMemo(() => calcAt(price, f), [price, f]);
  const solved = useMemo(() => solvePrices(f, f.targetMargin), [f]);
  const ladder = useMemo(() => priceLadder(f), [f]);
  const monthly = result.profit * num(f.monthlyQty);
  const cls = (v) => (v < 0 ? 'neg' : 'pos');

  const inputNum = (key, props = {}) => (
    <input type="number" step="any" value={f[key]} onChange={(e) => set({ [key]: e.target.value })} {...props} />
  );

  const importProduct = (uid) => {
    const p = products.find((x) => x.productUid === uid);
    if (!p) return;
    set({ name: p.productNm, cost: String(Math.round(p.headCost || 0)) });
    showAlert(`${p.productNm} — 원가에 현재 FIFO 단가(₩${fmt(Math.round(p.headCost || 0))}/${p.useUnit})를 넣었습니다.`, 'info');
  };

  const persist = (next) => {
    saveTable(tc, TABLE, next);
    setItems(next);
  };
  const saveItem = () => {
    if (!f.name.trim()) return showAlert('상품명을 입력하세요.', 'warning');
    if (price <= 0) return showAlert('판매가를 입력하세요.', 'warning');
    const existing = items.find((i) => i.name === f.name.trim());
    const row = { ...f, name: f.name.trim(), itemUid: existing?.itemUid || createUid('MRG'), savedAt: new Date().toISOString() };
    persist(existing ? items.map((i) => (i.itemUid === existing.itemUid ? row : i)) : [...items, row]);
    return showAlert(existing ? '같은 이름의 저장 항목을 갱신했습니다.' : '저장했습니다.', 'success');
  };
  const delItem = (i) => {
    if (window.confirm(`${i.name} 저장 항목을 삭제할까요?`)) persist(items.filter((x) => x.itemUid !== i.itemUid));
  };

  const saved = useMemo(
    () => items.map((i) => ({ ...i, res: calcAt(num(i.price), i) })).sort((a, b) => b.res.margin - a.res.margin),
    [items]
  );

  return (
    <PageContainer>
      <Wrap>
        <div className="box">
          <div className="title">입력</div>
          <div className="f">
            <Field label="제품 불러오기" hint="제품마스터의 현재 FIFO 단가를 원가로 채웁니다">
              <select value="" onChange={(e) => importProduct(e.target.value)}>
                <option value="">선택 (선택사항)</option>
                {products.map((p) => <option key={p.productUid} value={p.productUid}>{p.productCode} {p.productNm}</option>)}
              </select>
            </Field>
            <Field label="상품명"><input type="text" value={f.name} onChange={(e) => set({ name: e.target.value })} /></Field>
            <Field label="판매가 (VAT 포함)">{inputNum('price')}</Field>
            <Field label="개당 매입원가">{inputNum('cost')}</Field>
            <Field label="원가 VAT">
              <select value={f.costVatIncl ? 'Y' : 'N'} onChange={(e) => set({ costVatIncl: e.target.value === 'Y' })}>
                <option value="Y">포함 (세금계산서 매입)</option>
                <option value="N">별도 / 공급가</option>
              </select>
            </Field>
            <Field label="구성 수량" hint="한 상품(세트)에 들어가는 개수 — 원가 × 구성수량">{inputNum('qtyPerSet', { min: 1 })}</Field>
            <Field label="판매수수료율(%)" hint="카테고리별로 다름 — 쿠팡 셀러 수수료 안내 기준으로 입력. VAT 별도 청구로 계산">{inputNum('feeRate')}</Field>
            <Field label="배송·물류비(건당)" hint="로켓그로스 입출고·배송비 또는 택배비, VAT 별도 금액">{inputNum('shipFee')}</Field>
            <Field label="광고비율(%)" hint="판매가 대비, VAT 별도">{inputNum('adRate')}</Field>
            <Field label="포장·기타(건당)">{inputNum('packFee')}</Field>
            <Field label="반품·CS 충당(%)" hint="판매가 대비 평균 손실 추정치">{inputNum('returnRate')}</Field>
            <Field label="과세 유형">
              <select value={f.taxMode} onChange={(e) => set({ taxMode: e.target.value })}>
                <option value="GENERAL">일반과세 (매입세액 공제)</option>
                <option value="SIMPLE">간이·기타 (공제 없음)</option>
              </select>
            </Field>
            {f.taxMode === 'SIMPLE' && (
              <Field label="납부 부가세(%)" hint="판매가 대비 부담률. 예) 소매 부가가치율 15% × 10% = 1.5% — 업종·매출에 따라 다르니 세무사 확인">
                {inputNum('simpleTaxRate')}
              </Field>
            )}
            <Field label="목표 마진율(%)">{inputNum('targetMargin')}</Field>
            <Field label="월 예상 판매수량">{inputNum('monthlyQty')}</Field>
          </div>
          <div className="row">
            <ActionButton type="button" onClick={saveItem}>💾 저장(비교 목록에 추가)</ActionButton>
            <ActionButton type="button" onClick={() => setF(DEFAULTS)}>초기화</ActionButton>
          </div>
          <span className="hint">세전 관리용 계산입니다. 소득세·정산 시점 차이 등은 반영하지 않습니다.</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="box">
            <div className="title">결과 (1건 기준)</div>
            <div className="kpis">
              <div className="kpi"><div className="l">순이익</div><div className={`v ${cls(result.profit)}`}>{won(result.profit)}</div></div>
              <div className="kpi"><div className="l">마진율 (판매가 대비)</div><div className={`v ${cls(result.margin)}`}>{pctText(result.margin)}</div></div>
              <div className="kpi"><div className="l">수익률 (원가 대비)</div><div className={`v ${cls(result.roi)}`}>{pctText(result.roi)}</div></div>
              <div className="kpi"><div className="l">월 예상 이익</div><div className={`v ${cls(monthly)}`}>{won(monthly)}</div></div>
            </div>
            <table>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className={r.kind === 'total' ? 'total' : r.kind === 'sub' || r.kind === 'base' ? 'sub' : ''}>
                    <td>{r.label}</td>
                    <td className="n">{won(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="row">
              <span>손익분기 판매가 <b>{solved.breakEven == null ? '계산 불가' : won(solved.breakEven)}</b></span>
              <span>· 마진 {num(f.targetMargin)}% 달성 판매가 <b>{solved.target == null ? '계산 불가 (변동비율이 너무 큼)' : won(solved.target)}</b></span>
              {solved.target != null && (
                <ActionButton type="button" onClick={() => set({ price: String(solved.target) })}>이 가격 적용</ActionButton>
              )}
            </div>
          </div>

          <div className="box">
            <div className="title">판매가 시나리오</div>
            <table>
              <thead><tr><th>변동</th><th>판매가</th><th>순이익</th><th>마진율</th><th>수익률</th></tr></thead>
              <tbody>
                {ladder.map((r) => (
                  <tr key={r.step} className={r.step === 0 ? 'cur' : ''}>
                    <td>{r.step === 0 ? '현재' : `${r.step > 0 ? '+' : ''}${Math.round(r.step * 100)}%`}</td>
                    <td className="n">{won(r.price)}</td>
                    <td className={`n ${r.profit < 0 ? 'warn' : ''}`}>{won(r.profit)}</td>
                    <td className="n">{pctText(r.margin)}</td>
                    <td className="n">{pctText(r.roi)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="box">
            <div className="title">저장한 상품 비교 ({saved.length}) — 마진율 높은 순</div>
            <table>
              <thead><tr><th>상품명</th><th>판매가</th><th>원가</th><th>순이익</th><th>마진율</th><th>수익률</th><th /></tr></thead>
              <tbody>
                {saved.length === 0 && <tr><td colSpan={7} className="hint">저장한 상품이 없습니다.</td></tr>}
                {saved.map((i) => (
                  <tr key={i.itemUid}>
                    <td>{i.name}</td>
                    <td className="n">{won(num(i.price))}</td>
                    <td className="n">{won(i.res.costTotal)}</td>
                    <td className={`n ${i.res.profit < 0 ? 'warn' : ''}`}>{won(i.res.profit)}</td>
                    <td className="n">{pctText(i.res.margin)}</td>
                    <td className="n">{pctText(i.res.roi)}</td>
                    <td>
                      <div className="row">
                        <ActionButton type="button" onClick={() => setF({ ...DEFAULTS, ...i })}>불러오기</ActionButton>
                        <ActionButton type="button" onClick={() => delItem(i)}>삭제</ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Wrap>
    </PageContainer>
  );
};

export default MarginCalculator;
